import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { StageList, StudioStage, identityCode, identityError, identityRequest } from "../../services/physicalIdentityApi";
import "./scan.scss";

type Row = { code?: string; label: string; active: boolean; system: boolean; side: boolean; key: string };
const toRows = (stages: StudioStage[]): Row[] => stages.map(s => ({ ...s, key: s.code }));

/**
 * The studio's production stages after the loom (US-003). The studio admin renames, adds, reorders and switches
 * stages off; nothing is deleted, so older journey steps keep their place. Weaving (from the loom records) stays
 * first and Production completed stays last; "In restoration" sits outside the line. Others see the list read-only.
 */
export default function StageListEditor() {
  const history = useHistory();
  const [list, setList] = useState<StageList>();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: "status" | "alert"; text: string }>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await identityRequest<StageList>("/stage-list");
      setList(next); setRows(toRows(next.stages));
    } catch (e) { setMessage({ kind: "alert", text: identityError(e) }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const editable = Boolean(list?.editable);
  const line = rows.filter(r => !r.side), side = rows.filter(r => r.side);
  const update = (key: string, patch: Partial<Row>) => setRows(all => all.map(r => (r.key === key ? { ...r, ...patch } : r)));
  // Only the stages between Weaving and Production completed move.
  const move = (key: string, by: -1 | 1) => setRows(all => {
    const main = all.filter(r => !r.side), rest = all.filter(r => r.side);
    const i = main.findIndex(r => r.key === key), j = i + by;
    if (i <= 0 || j <= 0 || j >= main.length - 1) return all;
    const next = [...main]; [next[i], next[j]] = [next[j], next[i]];
    return [...next, ...rest];
  });
  const add = () => setRows(all => {
    const main = all.filter(r => !r.side), rest = all.filter(r => r.side);
    const row: Row = { label: "", active: true, system: false, side: false, key: `new-${Date.now()}` };
    return [...main.slice(0, -1), row, main[main.length - 1], ...rest];
  });
  const save = async () => {
    setBusy(true); setMessage(undefined);
    try {
      const body = { expectedVersion: list?.version ?? null, stages: rows.map(r => ({ code: r.code || null, label: r.label.trim(), active: r.active })) };
      const next = await identityRequest<StageList>("/stage-list", body, undefined, "PUT");
      setList(next); setRows(toRows(next.stages));
      setMessage({ kind: "status", text: "Saved. Scans use these stages now." });
    } catch (e) {
      if (identityCode(e) === "STAGE_LIST_CHANGED") await load();
      setMessage({ kind: "alert", text: identityError(e) });
    } finally { setBusy(false); }
  };

  const rowEl = (r: Row, i: number, of: number) => {
    const fixed = r.system || r.side;
    return <li key={r.key} className={`stage-row${r.active ? "" : " is-off"}`}>
      <input className="stage-row__name" aria-label="Stage name" value={r.label} maxLength={40} disabled={!editable}
        onChange={e => update(r.key, { label: e.target.value })} placeholder="Stage name" />
      {editable && !fixed && <div className="stage-row__tools">
        <button type="button" className="scan-btn is-icon" aria-label={`Move ${r.label || "stage"} up`} disabled={i <= 1} onClick={() => move(r.key, -1)}>↑</button>
        <button type="button" className="scan-btn is-icon" aria-label={`Move ${r.label || "stage"} down`} disabled={i >= of - 2} onClick={() => move(r.key, 1)}>↓</button>
        <label className="stage-row__on"><input type="checkbox" aria-label={`${r.label || "New stage"} is on`} checked={r.active} onChange={e => update(r.key, { active: e.target.checked })} /> On</label>
      </div>}
      {fixed && <span className="scan-meta">{r.code === "WEAVING" ? "From the loom" : r.side ? "Any time" : "Always last"}</span>}
    </li>;
  };

  return <main className="scan-page stage-editor">
    <div className="stage-editor__head">
      <button type="button" className="scan-btn is-icon" aria-label="Back" onClick={() => history.goBack()}>‹</button>
      <h1 className="scan-title">Production stages</h1>
    </div>
    <p className="scan-lede">The steps a saree goes through after the loom. A scan offers the next one. Stages are never deleted: switch one off and older journeys still show it.</p>
    {loading && <p role="status" className="scan-note">Loading stages…</p>}
    {message && <p role={message.kind} className={`scan-move__note${message.kind === "alert" ? " is-alert" : ""}`}>{message.text}</p>}
    {!loading && list && <>
      <ol className="stage-list">{line.map((r, i) => rowEl(r, i, line.length))}</ol>
      {editable && <button type="button" className="scan-btn is-add" onClick={add}>+ Add stage</button>}
      {side.length > 0 && <><h2 className="stage-editor__side">Out of the line</h2><ul className="stage-list">{side.map((r, i) => rowEl(r, i, side.length))}</ul></>}
      {editable ? <div className="scan-actions"><button type="button" className="scan-btn is-primary" disabled={busy || rows.some(r => !r.label.trim())} onClick={() => void save()}>{busy ? "Saving…" : "Save stages"}</button></div>
        : <p className="scan-note">Only the studio admin can change these.</p>}
    </>}
  </main>;
}
